import { c } from 'ttag';

import { refusalCode } from '../../graphql/refusalCode';
import type { Refusal } from './portalAccess';

/**
 * Reading of the refusals of `setAccountMfaRequirement`.
 *
 * **The seventh vocabulary of the repository, and it covers its mutation whole.** Six
 * codes and a generic branch, checked against the mutation's own description rather
 * than against the most visible `throw`: the authorisation function it calls adds
 * most of them, and an inventory by grepping the service file undercounts. The lesson
 * is PCG-110's, where four codes out of twenty were read and the generic branch said
 * « nothing here says what to do » **above** a server sentence that said precisely
 * what to do.
 *
 * `Refusal` is imported rather than declared again: seven vocabularies over one shape
 * is the point, and a second definition is how two of them would drift.
 *
 * **No `detail` on any read code here, and that is a decision.** Unlike `inviteUser`,
 * this mutation carries no fact in its message that the front does not already hold:
 * it acts on one account, named on screen, and its refusals are about the actor or
 * about the nature of that account. A server sentence under an operator sentence
 * would read as the two disagreeing.
 */
export const refusalOf = (error: unknown): Refusal => {
    switch (refusalCode(error)) {
        case 'FORBIDDEN_OUT_OF_SCOPE':
            return {
                title: c('MFA requirement refusal').t`This account is outside your portfolio.`,
                guidance: c('MFA requirement refusal')
                    .t`Requiring multi-factor authentication is reserved to whoever carries the account. Ask an administrator whose portfolio holds it.`,
            };
        case 'INTERNAL_STAFF_ONLY':
            return {
                title: c('MFA requirement refusal').t`This gesture belongs to the back-office.`,
                guidance: c('MFA requirement refusal')
                    .t`A security policy on a client account is a Freelance.com decision, so a client-side role cannot carry it whatever its permissions.`,
            };
        case 'MISSING_PERMISSION':
            return {
                title: c('MFA requirement refusal').t`Your role does not carry this gesture.`,
                guidance: c('MFA requirement refusal')
                    .t`Managing accounts is a back-office permission. Ask an administrator to set the requirement, or to grant you that permission.`,
            };
        case 'INTERNAL_ORGANIZATION_NOT_ALLOWED':
            return {
                title: c('MFA requirement refusal').t`Freelance.com is not a client account.`,
                guidance: c('MFA requirement refusal')
                    .t`The requirement is set on client accounts. Multi-factor authentication for colleagues is decided on the pool, not here.`,
            };
        case 'ORGANIZATION_NOT_FOUND':
            return {
                title: c('MFA requirement refusal').t`This account no longer exists.`,
                guidance: c('MFA requirement refusal')
                    .t`It has probably disappeared from the source since this screen was loaded. Reload the page to see the accounts as they are now.`,
            };
        case 'NOT_AUTHENTICATED':
            return {
                title: c('MFA requirement refusal').t`Your session no longer carries an active membership.`,
                guidance: c('MFA requirement refusal').t`Sign in again, then start the gesture over.`,
            };
        default:
            return {
                title: c('MFA requirement refusal').t`The server refused this action.`,
                guidance: c('MFA requirement refusal')
                    .t`This refusal is not one the screen knows how to read, so nothing here says what to do about it. The server's answer is below: it is what the support desk needs.`,
                detail: error instanceof Error ? error.message : String(error),
            };
    }
};
